import {
  ExportFormat,
  ExportOptions,
  ExportPayloadSection,
  SerializeExportProgress,
} from '../types';
import { CSV, JSON_FORMAT, TXT, XML, XLSX } from '../constants';
import buildDelimitedContent from './csv/buildDelimitedContent';
import buildJsonContent from './json/buildJsonContent';
import buildXlsxWorkbook from './xlsx/buildXlsxWorkbook';
import buildXmlContent from './xml/buildXmlContent';
import countTotalRows from './countTotalRows';
import encodeText from './encodeText';

export interface SerializeExportResult {
  bytes: Uint8Array;
  extension: ExportFormat;
  mimeType: string;
}

export const serializeExportContent = async (
  sections: ExportPayloadSection[],
  options: ExportOptions,
  onProgress?: SerializeExportProgress,
  yieldEveryNRows: number = Infinity
): Promise<SerializeExportResult> => {
  const totalRows = countTotalRows(sections);
  const context = {
    processedRows: 0,
    totalRows,
    onProgress,
    yieldEveryNRows,
  };

  // Initial 0% snapshot lets the UI render a determinate bar before the first row finishes.
  onProgress?.(0, totalRows);

  if (options.format === TXT) {
    const content = await buildDelimitedContent(sections, options, context);
    onProgress?.(context.processedRows, totalRows);
    return {
      bytes: encodeText(content, options.encoding),
      extension: TXT,
      mimeType: 'text/plain',
    };
  }

  if (options.format === CSV) {
    const content = await buildDelimitedContent(sections, options, context);
    onProgress?.(context.processedRows, totalRows);
    return {
      bytes: encodeText(content, options.encoding),
      extension: CSV,
      mimeType: 'text/csv',
    };
  }

  if (options.format === XML) {
    const content = await buildXmlContent(sections, options, context);
    onProgress?.(context.processedRows, totalRows);
    return {
      bytes: encodeText(content, options.encoding),
      extension: XML,
      mimeType: 'application/xml',
    };
  }

  if (options.format === JSON_FORMAT) {
    const content = await buildJsonContent(sections, options, context);
    onProgress?.(context.processedRows, totalRows);
    // JSON is UTF-8 by spec, so we skip the encoding rewrite path.
    return {
      bytes: new TextEncoder().encode(content),
      extension: JSON_FORMAT,
      mimeType: 'application/json',
    };
  }

  // XLSX is the default; write-excel-file emits a real OOXML zip so no encoding rewrite applies.
  const bytes = await buildXlsxWorkbook(sections, options, context);
  onProgress?.(context.processedRows, totalRows);
  return {
    bytes,
    extension: XLSX,
    mimeType:
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  };
};
